/* Runs a student's JavaScript against a list of trials.

   The code runs in a Web Worker. That buys two things that matter in a
   classroom: no access to the page's DOM, and `terminate()` as a way out of an
   infinite loop — which first-year students write constantly.

   It is NOT a security sandbox. The worker is same-origin and this is not a
   defence against hostile code; it is a guard against the ordinary accidents of
   learning. Students run their own work here, so that is the right trade. If
   this is ever opened to untrusted submissions, move execution server-side.

   `workerMain` is a real function rather than a source string so that it is
   parsed and checked like any other code, and can be tested directly. It is
   serialised with toString() to build the worker — so it must not close over
   anything in this module.
*/

export function workerMain() {
  self.onmessage = function (e) {
    var code = e.data.code, exportName = e.data.exportName, tests = e.data.tests || [];
    var logs = [];

    /* Student code runs in strict mode, as their homework does (every template
       is an ES module) and as the lessons assume (D-073). The directive is
       added here, never shown in the editor, and sits on the student's first
       line so no line number moves. */
    var STRICT = '"use strict"; ';

    /* JSON-shaped, so `[1,2]` and `{"a":1}` print as content authors expect —
       but the values JSON cannot represent print as themselves. JSON.stringify
       printed `undefined` as nothing and NaN as `null`, which is exactly the
       output a `run` step exists to show a student truthfully. */
    function show(v, seen) {
      if (v === undefined) return 'undefined';
      if (typeof v === 'number') return Number.isFinite(v) ? JSON.stringify(v) : String(v);
      if (typeof v === 'bigint') return v + 'n';
      if (typeof v === 'symbol' || typeof v === 'boolean' || v === null) return String(v);
      if (typeof v === 'string') return JSON.stringify(v);
      if (typeof v === 'function') return '[Function: ' + (v.name || 'anonymous') + ']';
      if (seen.indexOf(v) >= 0) return '[Circular]';
      seen.push(v);
      var out = Array.isArray(v)
        ? '[' + v.map(function (x) { return show(x, seen); }).join(',') + ']'
        : '{' + Object.keys(v).map(function (k) { return JSON.stringify(k) + ':' + show(v[k], seen); }).join(',') + '}';
      seen.pop();
      return out;
    }
    function fmt(v) {
      try { return typeof v === 'string' ? v : show(v, []); }
      catch (err) { return String(v); }
    }
    function record(kind) {
      return function () {
        logs.push({ text: Array.prototype.map.call(arguments, fmt).join(' '), kind: kind });
      };
    }
    var sandboxConsole = { log: record('log'), error: record('err'), warn: record('err'), info: record('log') };
    self.console = sandboxConsole;

    /* Program mode: no export, no tests — run it and report what it printed.
       The `run` practice rung needs this: the student predicts, then sees what
       the code actually does (02-practice rule 1). */
    if (!exportName) {
      /* Report only once the program has gone quiet. Posting straight after the
         synchronous code returned lost everything a promise or timer printed —
         the worker is terminated the moment its message arrives — so an async
         `run` step showed only its first line. Timers are counted through
         wrappers passed in as parameters; microtasks drain before any timer
         callback, so "no timer pending when a 0 ms check fires" means done.
         A program that never goes quiet hits the caller's timeout. */
      var pending = 0, failed = null;
      var describe = function (err) {
        return ((err && err.name) ? err.name + ': ' : '') + ((err && err.message) || String(err));
      };
      var wrapT = function (fn, args) {
        pending++;
        return setTimeout(function () {
          pending--;
          try { fn.apply(null, args); } catch (err) { failed = failed || describe(err); }
        }, args.delay);
      };
      var st = function (fn, delay) {
        var extra = Array.prototype.slice.call(arguments, 2);
        return wrapT(fn, Object.assign(extra, { delay: delay || 0 }));
      };
      var ct = function (id) { if (id != null) { clearTimeout(id); pending = Math.max(0, pending - 1); } };
      if (typeof self.addEventListener === 'function') {
        self.addEventListener('unhandledrejection', function (e) {
          failed = failed || ('Uncaught (in promise) ' + describe(e.reason));
        });
      }
      try {
        new Function('console', 'setTimeout', 'clearTimeout', STRICT + code)(sandboxConsole, st, ct);
      } catch (err) {
        self.postMessage({ ok: false, logs: logs, results: [], error: describe(err) });
        return;
      }
      var settle = function () {
        if (pending > 0) { setTimeout(settle, 5); return; }
        self.postMessage({ ok: !failed, error: failed, logs: logs, results: [] });
      };
      setTimeout(settle, 0);
      return;
    }

    var subject;
    try {
      // `console` is passed in explicitly rather than relying on self.console
      // replacing the global — that only holds where `self` IS the global.
      subject = new Function(
        'console',
        STRICT + code + '\n;return typeof ' + exportName + ' === "undefined" ? undefined : ' + exportName + ';'
      )(sandboxConsole);
    } catch (err) {
      self.postMessage({
        ok: false, logs: logs, results: [],
        error: (err && err.message) || String(err),
      });
      return;
    }

    if (typeof subject === 'undefined') {
      self.postMessage({
        ok: false, logs: logs, results: [],
        error: 'Could not find ' + exportName + '. Make sure it is declared at the top level.',
      });
      return;
    }

    /* Test bodies are async functions, and each result is awaited, so a test
       can `await` the student's promise or timer. Until 2026-09-27 a body ran
       synchronously and its result was coerced with `!!` — a returned promise
       is truthy, so any test of async code passed whatever the code did, and
       async practice had to be tested through synchronous stand-ins. Each test
       gets TEST_MS to settle; one that never does fails on its own rather than
       timing out the whole run and losing every other result. */
    var TEST_MS = 1000;
    var AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
    var runTest = function (body) {
      return new Promise(function (resolve) {
        var timer = setTimeout(function () {
          resolve({ pass: false, error: 'Did not finish within ' + TEST_MS + 'ms — is a promise never settled?' });
        }, TEST_MS);
        var done = function (r) { clearTimeout(timer); resolve(r); };
        try {
          new AsyncFunction('subject', 'console', body)(subject, sandboxConsole).then(
            function (v) { done({ pass: !!v, error: null }); },
            function (err) { done({ pass: false, error: (err && err.message) || String(err) }); }
          );
        } catch (err) {
          done({ pass: false, error: (err && err.message) || String(err) });   // a test body that does not parse
        }
      });
    };
    var results = [];
    var next = function (i) {
      if (i >= tests.length) {
        self.postMessage({ ok: true, error: null, logs: logs, results: results });
        return;
      }
      runTest(tests[i].body).then(function (r) { results.push(r); next(i + 1); });
    };
    next(0);
  };
}

const WORKER_SRC = `(${workerMain.toString()})();`;

/**
 * @returns {Promise<{ok:boolean, error:string|null, logs:Array, results:Array, timedOut?:boolean}>}
 */
/** Run a whole program and collect what it printed. */
export const runProgram = ({ code, timeout = 2000 }) => runTrials({ code, exportName: null, tests: [], timeout });

/* The default timeout grows with the test count: each test may take up to
   TEST_MS (in workerMain) to settle, and the whole run must not be killed while
   a slow-but-finite test is still being awaited. An endless synchronous loop
   is still stopped — later, not never. */
export function runTrials({ code, exportName, tests, timeout = 2000 + (tests?.length || 0) * 1000 }) {
  return new Promise(resolve => {
    let url, worker, timer;
    const cleanup = () => {
      clearTimeout(timer);
      try { worker?.terminate(); } catch {}
      if (url) URL.revokeObjectURL(url);
    };

    try {
      url = URL.createObjectURL(new Blob([WORKER_SRC], { type: 'text/javascript' }));
      worker = new Worker(url);
    } catch (err) {
      return resolve({ ok: false, error: 'Could not start the runner: ' + err.message, logs: [], results: [] });
    }

    timer = setTimeout(() => {
      cleanup();
      resolve({
        ok: false, timedOut: true, logs: [], results: [],
        error: `Timed out after ${timeout}ms — check for a loop that never ends.`,
      });
    }, timeout);

    worker.onmessage = e => { cleanup(); resolve(e.data); };
    worker.onerror = e => {
      cleanup();
      resolve({ ok: false, error: e.message || 'The runner crashed.', logs: [], results: [] });
    };

    worker.postMessage({ code, exportName, tests });
  });
}
